import { ScrollText, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { useApiCollection } from '../../lib/useApiCollection';
import type { AuditEvent, CursorPaginatedResponse } from '../../types/domain';

const pageSize = 25;

const timestampFormatter = new Intl.DateTimeFormat('en-PK', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Karachi',
});

function readable(value: string) {
  return value.replaceAll('.', ' · ').replaceAll('_', ' ');
}

function changeSummary(event: AuditEvent) {
  const section = event.changes.changed ?? event.changes.created ?? event.changes.deleted ?? event.changes;
  if (!section || typeof section !== 'object' || Array.isArray(section)) {
    const statusCode = event.changes.status_code;
    return typeof statusCode === 'number' ? `HTTP ${statusCode}` : 'No field details';
  }
  const fields = Object.keys(section);
  return fields.length > 0 ? fields.map((field) => field.replaceAll('_', ' ')).join(', ') : 'No field details';
}

function cursorFromLink(link: string | null) {
  if (!link) return null;
  return new URL(link).searchParams.get('cursor');
}

export function AuditLogPage() {
  const { accessToken, selectedWorkspace } = useAuth();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [outcome, setOutcome] = useState('');
  const [entityType, setEntityType] = useState('');
  const [page, setPage] = useState(1);
  const [cursor, setCursor] = useState<string | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const params = new URLSearchParams({ page_size: String(pageSize) });
  if (cursor) params.set('cursor', cursor);
  if (debouncedSearch) params.set('search', debouncedSearch);
  if (outcome) params.set('outcome', outcome);
  if (entityType) params.set('entity_type', entityType);
  if (selectedWorkspace?.organizationId) {
    params.set('workspace', selectedWorkspace.organizationId);
  }
  const events = useApiCollection<AuditEvent, CursorPaginatedResponse<AuditEvent>>(
    'audit-events',
    `/audit-events/?${params.toString()}`,
    accessToken,
  );
  const rows = events.data?.results ?? [];

  function resetPosition() {
    setCursor(null);
    setPage(1);
  }

  function resetFilters() {
    setSearch('');
    setDebouncedSearch('');
    setOutcome('');
    setEntityType('');
    resetPosition();
  }

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">{selectedWorkspace?.name}</p>
          <h1>Audit log</h1>
          <p>Review immutable operational activity without exposing private payloads.</p>
        </div>
        <span className="record-count"><ScrollText size={16} /> Immutable history</span>
      </header>

      <section className="content-card attendance-filter-card">
        <div className="card-heading">
          <div><h2>Find activity</h2><p>Use an exact request/entity ID, action, actor, workspace, or record label.</p></div>
          <button className="text-button" onClick={resetFilters} type="button">Reset filters</button>
        </div>
        <div className="attendance-filter-grid audit-filter-grid">
          <label className="attendance-search-filter">
            Search
            <span>
              <Search aria-hidden="true" size={16} />
              <input
                aria-label="Search audit activity"
                onChange={(event) => { setSearch(event.target.value); resetPosition(); }}
                placeholder="Exact ID, action, actor, workspace or record"
                type="search"
                value={search}
              />
            </span>
          </label>
          <label>
            Outcome
            <select aria-label="Audit outcome" onChange={(event) => { setOutcome(event.target.value); resetPosition(); }} value={outcome}>
              <option value="">All outcomes</option>
              <option value="succeeded">Succeeded</option>
              <option value="rejected">Rejected</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label>
            Record type
            <select aria-label="Audit record type" onChange={(event) => { setEntityType(event.target.value); resetPosition(); }} value={entityType}>
              <option value="">All record types</option>
              <option value="campaign">Campaign</option>
              <option value="venue">Store</option>
              <option value="shift_schedule">Shift schedule</option>
              <option value="assignment">Assignment</option>
              <option value="survey_assignment">Survey authorization</option>
              <option value="attendance_decision">Attendance decision</option>
              <option value="sales_decision">Sales decision</option>
              <option value="shelf_evidence_decision">Shelf evidence decision</option>
              <option value="stock_receipt">Stock receipt</option>
              <option value="data_export">Data export</option>
              <option value="api_request">Rejected request</option>
            </select>
          </label>
        </div>
      </section>

      <DataPanel empty={rows.length === 0} error={events.isError} loading={events.isLoading}>
        <section className="content-card data-card">
          <div className="data-table-wrap">
            <table className="data-table audit-table">
              <thead><tr><th>Time</th><th>Actor</th><th>Action</th><th>Record</th><th>Workspace</th><th>Outcome</th><th>Safe changes</th></tr></thead>
              <tbody>
                {rows.map((event) => (
                  <tr key={event.id}>
                    <td><strong>{timestampFormatter.format(new Date(event.recorded_at))}</strong><small>{event.request_id.slice(0, 8)}…</small></td>
                    <td>{event.actor_label}</td>
                    <td>{readable(event.action)}</td>
                    <td><strong>{event.entity_label || readable(event.entity_type)}</strong><small>{event.entity_public_id?.slice(0, 8) ?? 'No public ID'}</small></td>
                    <td>{event.workspace_name ?? 'Platform-wide'}</td>
                    <td><StatusPill value={event.outcome} /></td>
                    <td>{changeSummary(event)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="attendance-pagination">
            <span>Page {page} · up to {pageSize} events per page</span>
            <div>
              <button className="button secondary" disabled={!events.data?.previous} onClick={() => { setCursor(cursorFromLink(events.data?.previous ?? null)); setPage((current) => Math.max(1, current - 1)); }} type="button">Previous</button>
              <button className="button secondary" disabled={!events.data?.next} onClick={() => { setCursor(cursorFromLink(events.data?.next ?? null)); setPage((current) => current + 1); }} type="button">Next</button>
            </div>
          </div>
        </section>
      </DataPanel>
    </>
  );
}
